import type { GridApi, IRowNode, RowDropTargetPosition, RowsDropParams } from "ag-grid-community";
import { nanoid } from "nanoid";

import type { ColumnRow } from "../ColumnSelectionGrid.tsx";

export interface RootDropPending {
  mode: "root-drop";
  childRowIds: string[];
  template: Pick<ColumnRow, "title" | "colId" | "groupName" | "hide">;
  position?: RowDropTargetPosition;
  originalParentId?: string;
  originalParentData?: ColumnRow;
  shouldRemoveOriginalParent?: boolean;
}

export interface GroupDropPending {
  mode: "group-drop";
  childRowIds: string[];
  originalParentId?: string;
  originalParentData?: ColumnRow;
}

export interface GroupMergePending {
  mode: "merge-groups";
  sourceGroupId: string;
  targetGroupId: string;
  targetGroupData: ColumnRow;
  dropPosition: RowDropTargetPosition | undefined;
}

export type PendingDrop = RootDropPending | GroupDropPending | GroupMergePending;

interface DropValidationResponse {
  allowed?: boolean;
  rows?: IRowNode<ColumnRow>[];
  highlight?: boolean;
  changed?: boolean;
  position?: RowDropTargetPosition;
}

interface DropValidationResult {
  response: DropValidationResponse;
  pendingDrop: PendingDrop | null;
}

// Ensures we delete a source group once the last child has been dragged to a new parent.
export const cleanUpSourceGroup = (
  parentRowId: string | undefined,
  parentData: ColumnRow | undefined,
  api: GridApi<ColumnRow>,
) => {
  if (!parentRowId || !parentData) {
    return;
  }

  const parentNode = api.getRowNode(parentRowId);
  const hasRemainingChildren =
    (parentNode?.childrenAfterGroup?.length ?? 0) > 0 || (parentNode?.allChildrenCount ?? 0) > 0;

  if (!hasRemainingChildren) {
    api.applyTransaction({
      remove: [parentData],
    });
  }
};

/**
 * Validates whether a drag target is acceptable and captures the resulting drop intentions.
 * We translate user intent into a stable pending operation so the caller can execute the move.
 */
export const evaluateDropPosition = ({
  moved,
  rows,
  newParentData,
  position,
  targetNode,
  overNode,
}: {
  moved: boolean;
  rows: IRowNode<ColumnRow>[];
  newParentData: ColumnRow | undefined;
  position: RowDropTargetPosition | undefined;
  targetNode: IRowNode<ColumnRow> | null | undefined;
  overNode: IRowNode<ColumnRow> | undefined;
}): DropValidationResult => {
  if (!moved) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  // AG Grid gives us nodes; capture the row data we actually need for decision making.
  const movingRowData = rows.map((row) => row.data).filter((row): row is ColumnRow => row != null);

  if (movingRowData.length === 0) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  // Decide which validation branch to run based on what kind of rows were dragged.
  const selectionKind = getSelectionKind(movingRowData);

  if (!selectionKind) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  switch (selectionKind) {
    case "group":
      return evaluateGroupRows({
        rows,
        movingRowData,
        newParentData,
        position,
        targetNode,
        overNode,
      });

    case "child":
      return evaluateChildRows({
        rows,
        movingRowData,
        newParentData,
        position,
      });

    case "root":
      return evaluateRootRows({
        rows,
        newParentData,
      });

    default:
      return { response: { allowed: false }, pendingDrop: null };
  }
};

type SelectionKind = "group" | "child" | "root";

// Determines whether the selection is made of groups, child rows, or root leaf rows.
const getSelectionKind = (movingRowData: ColumnRow[]): SelectionKind | null => {
  const hasGroupRows = movingRowData.some((row) => row.isGroup);
  const hasChildRows = movingRowData.some((row) => !row.isGroup && row.parentId);
  const hasRootRows = movingRowData.some((row) => !row.isGroup && !row.parentId);

  const selectionTypeCount = [hasGroupRows, hasChildRows, hasRootRows].filter(Boolean).length;

  if (selectionTypeCount !== 1) {
    return null;
  }

  if (hasGroupRows) {
    return "group";
  }

  if (hasChildRows) {
    return "child";
  }

  if (hasRootRows) {
    return "root";
  }

  return null;
};

/**
 * Handles drag validation for group rows. Only root-level reorders are allowed, with an optional merge step
 * when dropping a group onto another instance of the same groupName.
 */
const evaluateGroupRows = ({
  rows,
  movingRowData,
  newParentData,
  position,
  targetNode,
  overNode,
}: {
  rows: IRowNode<ColumnRow>[];
  movingRowData: ColumnRow[];
  newParentData: ColumnRow | undefined;
  position: RowDropTargetPosition | undefined;
  targetNode: IRowNode<ColumnRow> | null | undefined;
  overNode: IRowNode<ColumnRow> | undefined;
}): DropValidationResult => {
  // Groups can only be reordered at the root; attempts to nest under another parent are rejected.
  if (newParentData) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  const sourceGroup = movingRowData[0];
  const mergeTargetNode = resolveMergeTargetNode(targetNode, overNode, sourceGroup.id);

  if (movingRowData.length === 1 && mergeTargetNode?.data) {
    const targetData = mergeTargetNode.data;

    if (
      targetData.groupName != null &&
      sourceGroup.groupName != null &&
      targetData.groupName === sourceGroup.groupName
    ) {
      const resolvedTargetId = mergeTargetNode.id ?? targetData.id ?? targetData.colId;

      if (resolvedTargetId) {
        const pendingDrop: GroupMergePending = {
          mode: "merge-groups",
          sourceGroupId: sourceGroup.id,
          targetGroupId: resolvedTargetId,
          targetGroupData: targetData,
          dropPosition: position,
        };

        return {
          response: { rows, allowed: true, highlight: true, position: "inside" },
          pendingDrop,
        };
      }
    }
  }

  return { response: { rows }, pendingDrop: null };
};

/**
 * Handles child-row drags. Supports moving within the same group or reparenting to a new synthetic group at the root.
 */
const evaluateChildRows = ({
  rows,
  movingRowData,
  newParentData,
  position,
}: {
  rows: IRowNode<ColumnRow>[];
  movingRowData: ColumnRow[];
  newParentData: ColumnRow | undefined;
  position: RowDropTargetPosition | undefined;
}): DropValidationResult => {
  const referenceParentId = movingRowData[0]?.parentId;
  const referenceGroupName = movingRowData[0]?.groupName;

  // Child rows must all share the same source parent to avoid ambiguous reparenting.
  if (!referenceParentId) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  const allSameGroup = movingRowData.every(
    (row) => row.parentId === referenceParentId && row.groupName === referenceGroupName,
  );

  if (!allSameGroup) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  const sourceParentNode = rows[0]?.parent ?? undefined;
  const sourceParentData = sourceParentNode?.data;
  const originalParentId = sourceParentNode?.id ?? sourceParentData?.id;

  // Dropping children to the root converts them into a synthetic group that preserves their collective identity.
  if (!newParentData) {
    const originalParentChildren = sourceParentNode?.childrenAfterGroup ?? [];
    const remainingChildren = originalParentChildren.length - movingRowData.length;

    const pendingDrop: RootDropPending = {
      mode: "root-drop",
      childRowIds: movingRowData.map((row) => row.id),
      template: buildGroupTemplate(movingRowData, sourceParentData),
      position,
      originalParentId,
      originalParentData: originalParentId ? sourceParentData : undefined,
      shouldRemoveOriginalParent: remainingChildren <= 0,
    };

    return {
      response: { rows, allowed: true, position, highlight: true, changed: true },
      pendingDrop,
    };
  }

  if (!newParentData.isGroup) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  const matchesDestination =
    newParentData.id === referenceParentId ||
    (referenceGroupName != null && newParentData.groupName === referenceGroupName);

  if (!matchesDestination) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  if (newParentData.id !== referenceParentId) {
    const pendingDrop: GroupDropPending = {
      mode: "group-drop",
      childRowIds: movingRowData.map((row) => row.id),
      originalParentId,
      originalParentData: originalParentId ? sourceParentData : undefined,
    };

    return { response: { rows }, pendingDrop };
  }

  return { response: { rows }, pendingDrop: null };
};

/**
 * Handles root-level leaf rows. They can only be reordered at the root, never nested under a group.
 */
const evaluateRootRows = ({
  rows,
  newParentData,
}: {
  rows: IRowNode<ColumnRow>[];
  newParentData: ColumnRow | undefined;
}): DropValidationResult => {
  if (newParentData) {
    return { response: { allowed: false }, pendingDrop: null };
  }

  return { response: { rows }, pendingDrop: null };
};

/**
 * Moves a set of child rows under a newly created synthetic group, preserving their order and selection.
 * The pending drop context tells us which rows to relocate and where to insert the new group.
 */
export const reparentRowsToSyntheticGroup = (
  api: GridApi<ColumnRow>,
  rowsDrop: RowsDropParams<ColumnRow>,
  pendingDrop: RootDropPending,
) => {
  // Re-fetch nodes from the grid to avoid stale references before we make transactional updates.
  const childNodes = getRowNodesForIds(api, pendingDrop.childRowIds);

  if (childNodes.length === 0) {
    return;
  }

  // Snapshot selection so we can re-apply it once the rows move under the synthetic group.
  const originalSelections = childNodes.map((node) => ({
    id: node.id ?? node.data!.id,
    selected: node.isSelected(),
  }));
  // Row data gets reused after we remove the original entries.
  const originalData = childNodes.map((node) => node.data!) as ColumnRow[];
  // AG Grid wants the drop index relative to the full dataset; reconcile this with the rows being dragged.
  const absoluteDropIndex = resolveAbsoluteDropIndex(rowsDrop, pendingDrop.position, api);
  const addIndex = getDropAddIndex(childNodes, absoluteDropIndex);

  api.applyTransaction({
    remove: originalData,
  });

  // Insert the synthetic group at the appropriate index, then collect the runtime id AG Grid assigned.
  const groupRow = buildSyntheticGroup(pendingDrop);
  const transactionResult = api.applyTransaction({
    add: [groupRow],
    addIndex,
  });
  const createdGroupNode = transactionResult?.add?.[0];
  const resolvedGroupId = createdGroupNode?.id ?? groupRow.id;

  // Rebuild the child rows so they reference the newly created group and inherit its display name.
  const updatedChildren = originalData.map((data) => ({
    ...data,
    parentId: resolvedGroupId,
    groupName: groupRow.groupName,
  }));

  api.applyTransaction({
    add: updatedChildren,
  });

  const groupNode = api.getRowNode(resolvedGroupId);
  if (groupNode) {
    groupNode.setExpanded(true);
  }

  restoreSelection(api, updatedChildren, originalSelections);
  removeOrphanedSourceGroup(api, pendingDrop);
};

/**
 * Merges two sibling groups by moving all children from the source to the target and dropping the emptied source group.
 * Children inherit the target group's naming so the UI continues to display a single consolidated section.
 */
export const mergeGroups = (
  api: GridApi<ColumnRow>,
  rowsDrop: RowsDropParams<ColumnRow>,
  pendingDrop: GroupMergePending,
) => {
  const sourceGroupNode = api.getRowNode(pendingDrop.sourceGroupId);
  const targetGroupNode = api.getRowNode(pendingDrop.targetGroupId);

  if (!sourceGroupNode?.data || !targetGroupNode?.data) {
    return;
  }

  const sourceChildren = sourceGroupNode.childrenAfterGroup ?? [];
  const sourceChildData = sourceChildren.map((node) => node.data).filter((data): data is ColumnRow => data != null);

  const selectionSnapshots = sourceChildren.map<SelectionSnapshot>((node) => ({
    id: node.id ?? node.data?.id,
    selected: node.isSelected(),
  }));

  const targetGroupId = targetGroupNode.id ?? targetGroupNode.data.id;

  if (!targetGroupId) {
    return;
  }

  if (sourceChildData.length === 0) {
    api.applyTransaction({
      remove: [sourceGroupNode.data],
    });
    return;
  }

  const updatedChildren = sourceChildData.map((child) => ({
    ...child,
    parentId: targetGroupId,
    groupName: pendingDrop.targetGroupData.groupName ?? pendingDrop.targetGroupData.colId ?? child.groupName,
  }));

  api.applyTransaction({
    remove: [sourceGroupNode.data, ...sourceChildData],
  });

  const dropPositionRaw = pendingDrop.dropPosition ?? rowsDrop.position;
  const dropPosition =
    dropPositionRaw && dropPositionRaw !== "none" ? dropPositionRaw : ("inside" as RowDropTargetPosition);

  const refreshedTargetNode = api.getRowNode(targetGroupId);
  const baseRowIndex = refreshedTargetNode?.rowIndex ?? api.getDisplayedRowCount();
  const targetChildCount = refreshedTargetNode?.childrenAfterGroup?.length ?? 0;

  const addIndex =
    dropPosition === "above"
      ? Math.min(baseRowIndex + 1, api.getDisplayedRowCount())
      : Math.min(baseRowIndex + 1 + targetChildCount, api.getDisplayedRowCount());

  api.applyTransaction({
    add: updatedChildren,
    addIndex,
  });

  const expandedTargetNode = api.getRowNode(targetGroupId);
  if (expandedTargetNode) {
    expandedTargetNode.setExpanded(true);
  }

  restoreSelection(api, updatedChildren, selectionSnapshots);
};

// Builds the temporary group definition we will insert at the drop location.
const buildGroupTemplate = (
  rows: ColumnRow[],
  parent?: ColumnRow,
): Pick<ColumnRow, "title" | "colId" | "groupName" | "hide"> => {
  const fallbackGroupName = parent?.groupName ?? parent?.colId ?? rows[0]?.groupName ?? rows[0]?.colId;
  const fallbackTitle = parent?.title ?? rows[0]?.groupName ?? rows[0]?.title ?? "Group";

  return {
    title: fallbackTitle,
    colId: parent?.colId ?? rows[0]?.colId,
    groupName: fallbackGroupName ?? undefined,
    hide: parent?.hide,
  };
};

const resolveAbsoluteDropIndex = (
  rowsDrop: RowsDropParams<ColumnRow>,
  position: RowDropTargetPosition | undefined,
  api: GridApi<ColumnRow>,
): number => {
  const targetIndex = rowsDrop.target?.rowIndex ?? -1;
  const baseAnchorIndex =
    targetIndex >= 0 ? targetIndex : rowsDrop.overIndex >= 0 ? rowsDrop.overIndex : api.getDisplayedRowCount();

  if (position === "inside" || position === "below") {
    return baseAnchorIndex + 1;
  }

  return baseAnchorIndex;
};

// Collects row nodes originating from the dragged ids, ignoring rows AG Grid can no longer find.
const getRowNodesForIds = (api: GridApi<ColumnRow>, rowIds: string[]): IRowNode<ColumnRow>[] =>
  rowIds
    .map((rowId) => api.getRowNode(rowId))
    .filter((node): node is IRowNode<ColumnRow> => node != null && node.data != null);

// Converts a target drop position into the addIndex we can pass to AG Grid transactions.
const getDropAddIndex = (children: IRowNode<ColumnRow>[], index: number): number => {
  // Offset the add index by the number of dragged rows already residing before the drop anchor.
  const draggedRowsBeforeAnchor = children.reduce((count, node) => {
    const rowIndex = node.rowIndex ?? Number.POSITIVE_INFINITY;
    return rowIndex < index ? count + 1 : count;
  }, 0);

  return Math.max(index - draggedRowsBeforeAnchor, 0);
};

const buildSyntheticGroup = (pendingDrop: RootDropPending): ColumnRow => {
  const groupId = nanoid();

  return {
    id: groupId,
    isGroup: true,
    title: pendingDrop.template.title,
    colId: pendingDrop.template.colId,
    groupName: pendingDrop.template.groupName ?? pendingDrop.template.colId ?? groupId,
    hide: pendingDrop.template.hide,
  };
};

interface SelectionSnapshot {
  id: string | undefined;
  selected: boolean | undefined;
}

// Restores the selection state after we reinsert the dragged rows under their synthetic group.
const restoreSelection = (
  api: GridApi<ColumnRow>,
  updatedChildren: ColumnRow[],
  originalSelections: SelectionSnapshot[],
) => {
  updatedChildren.forEach((child) => {
    const node = api.getRowNode(child.id);
    if (!node) {
      return;
    }

    const wasSelected = originalSelections.find((item) => item.id === child.id)?.selected;
    if (wasSelected) {
      node.setSelected(true);
    }
  });
};

// Drops the now-empty parent that previously held the dragged rows if necessary.
const removeOrphanedSourceGroup = (api: GridApi<ColumnRow>, pendingDrop: RootDropPending) => {
  if (!pendingDrop.shouldRemoveOriginalParent || !pendingDrop.originalParentData) {
    return;
  }

  cleanUpSourceGroup(
    pendingDrop.originalParentId ?? pendingDrop.originalParentData.id,
    pendingDrop.originalParentData,
    api,
  );
};

function resolveMergeTargetNode(
  targetNode: IRowNode<ColumnRow> | null | undefined,
  overNode: IRowNode<ColumnRow> | undefined,
  sourceGroupId: string,
): IRowNode<ColumnRow> | null {
  const candidates: (IRowNode<ColumnRow> | null | undefined)[] = [
    targetNode,
    overNode,
    targetNode?.parent,
    overNode?.parent,
  ];

  for (const candidate of candidates) {
    if (!candidate?.data?.isGroup) {
      continue;
    }

    const candidateId = candidate.data.id ?? candidate.id;
    if (!candidateId || candidateId === sourceGroupId) {
      continue;
    }

    return candidate;
  }

  return null;
}
